// src/hooks/useAdminSync.js
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { beginGlobalSync, updateGlobalSync, endGlobalSync } from '../utils/globalSync';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
const ADMIN_HDR = ADMIN_KEY ? { headers: { 'X-Admin-Key': ADMIN_KEY } } : {};

function broadcastSyncState(payload) {
  window.dispatchEvent(new CustomEvent('admin-sync:state', { detail: payload }));
}

const LSM_POLL_MS = 1500;
// Max wall-clock the step-2 LSM poll loop will wait before giving up. The
// backend stale-run sweep flips a dead worker to 'error' after 30 min, so the
// poll loop should bail a bit before that to surface a timeout to the user.
const LSM_POLL_MAX_MS = 25 * 60 * 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useAdminSync() {
  const [run, setRun] = useState(null);        // last/active run payload
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const globalIdRef = useRef(null);
  const isMountedRef = useRef(true);

  // Button-facing state for the orchestrated 2-step full product sync.
  // step: 'products' (step 1) | 'lsm-sync' (step 2) | null (idle)
  // error: true after a failed/timed-out run (button shows "Sync failed").
  const [fullSync, setFullSync] = useState({
    running: false,
    step: null,
    label: null,
    progress: null,   // 0..1 or null (indeterminate)
    done: 0,
    total: 0,
    error: false,
  });

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pushBroadcast = useCallback((data) => {
    // Normalize payload for the header:
    // { status, id, summary, details, progress? }
    const payload = {
      status: data?.status,
      id: data?.id,
      summary: data?.summary || null,
      details: data?.details || null,
      progress: typeof data?.progress === 'number' ? data.progress : null,
    };
    broadcastSyncState(payload);
  }, []);

  const fetchRun = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/sync_runs/${id}`, ADMIN_HDR);
      if (!data?.ok) return;

      setRun(data);

      // Update global bar—use determinate if backend sends progress, else indeterminate
      if (typeof data.progress === 'number') {
        updateGlobalSync(globalIdRef.current, Math.max(0, Math.min(1, data.progress)));
      } else {
        updateGlobalSync(globalIdRef.current, null);
      }

      // Broadcast current state (running/summary)
      pushBroadcast(data);

      if (['done', 'error', 'cancelled'].includes(data.status)) {
        stopPolling();
        updateGlobalSync(globalIdRef.current, 1);
        endGlobalSync(globalIdRef.current);
        globalIdRef.current = null;
        setBusy(false);

        // Keep a terminal summary visible briefly, then clear
        setTimeout(() => broadcastSyncState(null), 5000);

        // Page-wide refresh hooks you already use
        if (data.status === 'done') {
          window.dispatchEvent(new CustomEvent('admin-sync:products-done', { detail: { runId: data.id } }));
          window.dispatchEvent(new CustomEvent('price-updated'));
        }
      }
    } catch (e) {
      // Network errors: stop polling & clear
      stopPolling();
      setBusy(false);
      if (globalIdRef.current) {
        endGlobalSync(globalIdRef.current);
        globalIdRef.current = null;
      }
      broadcastSyncState({ status: 'error' });
      setTimeout(() => broadcastSyncState(null), 5000);
    }
  }, [stopPolling, pushBroadcast]);

  // Resume a running job on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/admin/sync_runs/latest`, { params: { kind: 'products' }, ...ADMIN_HDR });
        const latest = data?.data;
        if (latest && latest.status === 'running') {
          setRun(latest);
          setBusy(true);
          globalIdRef.current = beginGlobalSync({ label: 'Product sync', job: 'products' });
          if (typeof latest.progress === 'number') {
            updateGlobalSync(globalIdRef.current, Math.max(0, Math.min(1, latest.progress)));
          } else {
            updateGlobalSync(globalIdRef.current, null);
          }
          pushBroadcast(latest);
          pollRef.current = setInterval(() => fetchRun(latest.id), 1500);
        }
      } catch { /* ignore */ }
    })();
    return () => {
      // Unmount guard: stop the interval poller AND signal the in-flight
      // step-2 while-loop (runFullProductSync) to bail so it doesn't keep
      // calling setFullSync / dispatching events after the component is gone.
      isMountedRef.current = false;
      stopPolling();
    };
  }, [fetchRun, stopPolling, pushBroadcast]);

  const runMarketSync = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    globalIdRef.current = beginGlobalSync({ label: 'Market sync', job: 'market' });
    pushBroadcast({ status: 'running', id: 'market' });
    try {
      await axios.post(`${API_BASE}/api/admin/market_prices/sync`, { dry_run: false }, ADMIN_HDR);
      updateGlobalSync(globalIdRef.current, 1);
      pushBroadcast({ status: 'done', id: 'market' });
      setTimeout(() => broadcastSyncState(null), 4000);
      window.dispatchEvent(new CustomEvent('price-updated'));
    } catch {
      pushBroadcast({ status: 'error', id: 'market' });
      setTimeout(() => broadcastSyncState(null), 5000);
    } finally {
      endGlobalSync(globalIdRef.current);
      globalIdRef.current = null;
      setBusy(false);
    }
  }, [busy, pushBroadcast]);

  const runProductSync = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    globalIdRef.current = beginGlobalSync({ label: 'Product sync', job: 'products' });
    updateGlobalSync(globalIdRef.current, null);
    pushBroadcast({ status: 'running' });
    try {
      const { data } = await axios.post(`${API_BASE}/api/admin/products/sync`, { limit: 200 }, ADMIN_HDR);
      if (data?.run_id) {
        const seed = { id: data.run_id, status: 'running', summary: {} };
        setRun(seed);
        pushBroadcast(seed);
        pollRef.current = setInterval(() => fetchRun(data.run_id), 1500);
      } else {
        // Immediate finish
        updateGlobalSync(globalIdRef.current, 1);
        pushBroadcast({ status: 'done' });
        setTimeout(() => broadcastSyncState(null), 4000);
        setBusy(false);
      }
    } catch {
      pushBroadcast({ status: 'error' });
      setTimeout(() => broadcastSyncState(null), 5000);
      setBusy(false);
    } finally {
      if (!pollRef.current && globalIdRef.current) {
        endGlobalSync(globalIdRef.current);
        globalIdRef.current = null;
      }
    }
  }, [busy, fetchRun, pushBroadcast]);

  // ── Orchestrated 2-step "Sync Products" sequence ──────────────────
  // Step 1: Product sync (Shopify pull + prune + batched market prices)
  // Step 2: LSM Control sync (dedicated threaded backfill, runs AFTER step 1)
  // Each step is a SEPARATE global-sync entry so the header panel shows two
  // distinct entries in order. Exposes `fullSync` for the ProgressButton.
  const runFullProductSync = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    let seqOk = true;  // false → surface the button error state at the end

    // ── Step 1 — Product sync (synchronous; runs Shopify pull + prices) ──
    const id1 = beginGlobalSync({ label: 'Product sync', job: 'products' });
    updateGlobalSync(id1, null);
    setFullSync({ running: true, step: 'products', label: 'Syncing products…', progress: null, done: 0, total: 0, error: false });
    try {
      await axios.post(`${API_BASE}/api/admin/products/sync`, { with_prices: true, limit: 200 }, ADMIN_HDR);
      updateGlobalSync(id1, 1);
    } catch (e) {
      console.error('Product sync (step 1) failed', e);
      seqOk = false;
    } finally {
      endGlobalSync(id1);
    }

    // ── Step 2 — LSM Control sync (dedicated threaded backfill) ──
    const id2 = beginGlobalSync({ label: 'LSM Control sync', job: 'lsm-sync' });
    updateGlobalSync(id2, null, { detail: 'Running' });
    setFullSync({ running: true, step: 'lsm-sync', label: 'LSM Control sync…', progress: null, done: 0, total: 0, error: false });
    let lsmOk = true;
    const lsmStartedAt = Date.now();
    try {
      const { data } = await axios.post(`${API_BASE}/api/admin/products/lsm-sync`, {}, ADMIN_HDR);
      const runId = data?.run_id;
      if (!runId) throw new Error('lsm-sync did not return a run_id');

      // Poll the run until it finishes; compute fraction from summary.done/total.
      // The `progress` column is an integer count — NOT a fraction.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await sleep(LSM_POLL_MS);
        // Unmount guard: component gone → stop touching state / dispatching.
        if (!isMountedRef.current) return;
        // Max-elapsed guard: never poll forever (dead worker, lost run).
        if (Date.now() - lsmStartedAt > LSM_POLL_MAX_MS) {
          console.error('LSM sync poll timed out after', LSM_POLL_MAX_MS, 'ms');
          lsmOk = false;
          break;
        }
        let status; let done = 0; let total = 0;
        try {
          const { data: r } = await axios.get(`${API_BASE}/api/admin/sync_runs/${runId}`, ADMIN_HDR);
          const summary = r?.summary || {};
          status = r?.status;
          done = Number(summary.done) || 0;
          total = Number(summary.total) || Number(r?.total) || 0;
        } catch (pollErr) {
          console.error('LSM sync poll failed', pollErr);
          lsmOk = false;
          break;
        }
        if (!isMountedRef.current) return;
        const frac = total ? done / total : null;
        const detail = total ? `${done} / ${total}` : 'Running';
        updateGlobalSync(id2, frac, { detail });
        setFullSync({ running: true, step: 'lsm-sync', label: 'LSM Control sync', progress: frac, done, total, error: false });
        if (status === 'done' || status === 'error' || status === 'cancelled') {
          if (status !== 'done') lsmOk = false;
          break;
        }
      }
      updateGlobalSync(id2, 1);
    } catch (e) {
      console.error('LSM Control sync (step 2) failed', e);
      lsmOk = false;
    } finally {
      endGlobalSync(id2);
    }

    // Unmount guard: bail before any post-sequence state / event work.
    if (!isMountedRef.current) return;

    if (!lsmOk) seqOk = false;

    // ── Done — refresh overview + product pages ──
    if (seqOk) {
      setFullSync({ running: false, step: null, label: null, progress: null, done: 0, total: 0, error: false });
    } else {
      // Surface a visible button error state; reset to idle after ~5s. The
      // button stays clickable in this state so the user can retry.
      setFullSync({ running: false, step: null, label: 'Sync failed', progress: null, done: 0, total: 0, error: true });
      setTimeout(() => {
        if (!isMountedRef.current) return;
        setFullSync({ running: false, step: null, label: null, progress: null, done: 0, total: 0, error: false });
      }, 5000);
    }
    setBusy(false);
    // NOTE: runFullProductSync drives the sync-center ONLY via the global-sync
    // entries above (beginGlobalSync/updateGlobalSync/endGlobalSync). It must NOT
    // also pushBroadcast(admin-sync:state) — that rendered a duplicate "Admin sync"
    // row alongside "Product sync"/"LSM Control sync" in the header panel.
    window.dispatchEvent(new CustomEvent('admin-sync:products-done', { detail: { source: 'full-sync', lsmOk } }));
    window.dispatchEvent(new CustomEvent('price-updated'));
  }, [busy]);

  const cancelRun = useCallback(async () => {
    if (!run?.id) return;
    try {
      await axios.post(`${API_BASE}/api/admin/sync_runs/${run.id}/cancel`, {}, ADMIN_HDR);
      // poller will pick up new status
    } catch {/* ignore */}
  }, [run]);

  return {
    busy,
    run,
    isRunning: run?.status === 'running',
    isDone: run?.status === 'done',
    cancelRun,
    runProductSync,
    runFullProductSync,
    fullSync,
    runMarketSync,
  };
}
